import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Collapse,
} from "@material-tailwind/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface DeleteModalProps {
  open: boolean;
  handleClose?: () => void;
  agentData: any;
}

const ServiceAgentDetailsPage: React.FC<DeleteModalProps> = ({ open, handleClose, agentData }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [openCollapse, setOpenCollapse] = useState({
    description: false,
    availability: false,
  });

  // Toggle collapse sections
  const toggleCollapse = (section: keyof typeof openCollapse) => {
    setOpenCollapse((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const fieldIcons = {
    designation: "las la-user-tag",
    fields: "las la-briefcase",
    region: "las la-map-marker-alt",
    phone: "las la-phone",
    contact: "las la-envelope",
    createdAt: "las la-calendar-plus",
    last_connection: "las la-clock",
    email: "las la-at",
    category: "las la-tag",
    availability: "las la-map",
    id: "las la-id-badge",
    userId: "las la-user",
    role: "las la-user-shield",
    subscription_status: "las la-credit-card",
    emailVirefied: "las la-check-circle",
    enable: "las la-toggle-on",
    updatedAt: "las la-calendar-check",
  };

  // Social media links
  const socialMedia = [
    {
      platform: "facebook",
      link: agentData?.facebook,
      icon: "lab la-facebook-square",
      color: "hover:bg-blue-600",
    },
    { platform: "youtube", link: agentData?.youtube, icon: "lab la-youtube", color: "hover:bg-red-600" },
    { platform: "instagram", link: agentData?.instagram, icon: "lab la-instagram", color: "hover:bg-pink-500" },
    { platform: "link", link: agentData?.link, icon: "lab la-chrome", color: "hover:bg-gray-500" },
  ].filter(({ link }) => link?.trim());

  // Tab data
  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Contact", value: "contact" },
    { label: "Détails avancés", value: "advanced" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => console.log}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Prestataire
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "Désignation", value: agentData?.designation, icon: fieldIcons.designation },
                { label: "Type de prestation", value: agentData?.fields, icon: fieldIcons.fields },
                { label: "Région", value: agentData?.region, icon: fieldIcons.region },
                { label: "Catégorie", value: agentData?.category, icon: fieldIcons.category },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}

              {/* Collapsible Availability */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("availability")}
                >
                  <i className={fieldIcons.availability}></i>
                  Disponibilité {openCollapse.availability ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.availability}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    <Typography className="text-gray-700 dark:text-gray-300">
                      {agentData?.availability?.length > 0
                        ? agentData.availability.join(", ")
                        : "Aucune information de disponibilité."}
                    </Typography>
                  </div>
                </Collapse>
              </div>

              {/* Collapsible Description */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("description")}
                >
                  <i className="las la-info-circle"></i>
                  Description {openCollapse.description ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.description}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    <Typography className="text-gray-700 whitespace-pre-line dark:text-gray-300">
                      {agentData?.description || "Aucune description disponible."}
                    </Typography>
                  </div>
                </Collapse>
              </div>
            </TabPanel>

            {/* Contact Information Tab */}
            <TabPanel value="contact" className="space-y-4">
              {[
                { label: "Numéro de téléphone", value: agentData?.phone, icon: fieldIcons.phone },
                { label: "Contact", value: agentData?.contact, icon: fieldIcons.contact },
                { label: "Email", value: agentData?.User?.email, icon: fieldIcons.email },
                {
                  label: "Dernière connexion",
                  value: agentData?.User?.last_connection
                    ? new Date(agentData.User.last_connection).toLocaleDateString("fr-FR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })
                    : "N/A",
                  icon: fieldIcons.last_connection,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
              {/* Social Media Links */}
              {socialMedia.length > 0 && (
                <div className="flex flex-wrap justify-center gap-4 p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                  {socialMedia.map(({ platform, link, icon, color }, index) => (
                    <a
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className={`flex items-center justify-center text-2xl rounded-full w-10 h-10 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white ${color} hover:text-white transform hover:scale-110 transition-all duration-200`}
                    >
                      <i className={icon}></i>
                    </a>
                  ))}
                </div>
              )}
            </TabPanel>

            {/* Advanced Details Tab */}
            <TabPanel value="advanced" className="space-y-4">
              {[
                { label: "ID du prestataire", value: agentData?.id, icon: fieldIcons.id },
                { label: "ID de l'utilisateur", value: agentData?.userId, icon: fieldIcons.userId },
                { label: "Rôle", value: agentData?.User?.role, icon: fieldIcons.role },
                {
                  label: "Statut de l'abonnement",
                  value: agentData?.User?.subscription_status,
                  icon: fieldIcons.subscription_status,
                },
                {
                  label: "Email vérifié",
                  value: agentData?.User?.emailVirefied ? "Oui" : "Non",
                  icon: fieldIcons.emailVirefied,
                },
                { label: "Compte activé", value: agentData?.enable ? "Oui" : "Non", icon: fieldIcons.enable },
                {
                  label: "Mis à jour le",
                  value: agentData?.updatedAt ? new Date(agentData.updatedAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.updatedAt,
                },
                {
                  label: "Inscrit le",
                  value: agentData?.createdAt ? new Date(agentData.createdAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.createdAt,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default ServiceAgentDetailsPage;
